// src/components/OptionsScreen.tsx
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BrowserProvider } from "ethers";
import { encryptAndMint, ProgressReport } from "../Client";
import { KeyserverStorage } from "../Storage";
import { SepoliaChain } from "../Chains";
import { AppContext } from "../context/AppContext";
import { SEPOLIA_PUBLIC_COLLECTION } from "../Client";
import { getProgressPercentage, Step } from "./ProgressBar";
import ProgressBar from "./ProgressBar";

const OptionsScreen: React.FC = () => {
  const [nftName, setNftName] = useState<string>("");
  const [price, setPrice] = useState<string>("");

  const { uploadedFile, setNftMinted, setNftBuyUrl } = useContext(AppContext);

  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [minting, setMinting] = useState<boolean>(false);
  const [mintProgress, setMintProgress] =
    useState<{ percentage: number; estimate?: Date } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (uploadedFile && uploadedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setThumbnail(e.target?.result as string);
      reader.readAsDataURL(uploadedFile);
    } else {
      setThumbnail(null);
    }
  }, [uploadedFile]);

  const handleMint = async () => {
    if (!nftName || !price || !uploadedFile) {
      alert("Please provide a name, price, and upload a file.");
      return;
    }

    setMinting(true);

    try {
      const storage = new KeyserverStorage();

      if (!window.ethereum) {
        throw new Error("MetaMask is not installed.");
      }

      const provider = new BrowserProvider(window.ethereum as any);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const chain = new SepoliaChain(signer);

      const metadata = {
        name: nftName,
        description: "Minted NFT from my app",
        image: thumbnail,
        image$confidential: uploadedFile,
      };

      // prevent double navigation if multiple progress updates arrive
      let navigated = false;

      const onProgress = (report: ProgressReport) => {
        const percentage = getProgressPercentage(report.step);
        setMintProgress({ percentage, estimate: report.estimate });
        console.log("Mint progress:", report);

        // Token ID is available once step > Minting
        if (!navigated && report.step > Step.Minting && report.tokenId != null) {
          const tokenIdStr = String(report.tokenId);
          setNftMinted(true);
          const dynamicBuyUrl = `${window.location.origin}/sepolia/${SEPOLIA_PUBLIC_COLLECTION}/${tokenIdStr}`;
          setNftBuyUrl(dynamicBuyUrl);
          navigated = true;
          navigate("/share");
        }

        // Optional: react to explicit rejection state
        if (report.step === Step.MetaMaskRejected && !navigated) {
          navigated = true;
          setMinting(false);
          alert("Transaction was rejected in MetaMask.");
        }
      };

      // encryptAndMint no longer returns tokenId; progress callback carries it
      await encryptAndMint(metadata, storage, chain, onProgress);
    } catch (error) {
      console.error("Minting failed", error);
      alert("Minting failed. Check console for details.");
    } finally {
      setMinting(false);
    }
  };

  return (
    <div className="card">
      <h2>Set NFT Options</h2>

      <div>
        <label>Name:</label>
        <input
          type="text"
          value={nftName}
          onChange={(e) => setNftName(e.target.value)}
          placeholder="Enter NFT name"
        />
      </div>

      <div>
        <label>Price:</label>
        <input
          type="text"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Enter price (e.g. 0.05 ETH)"
        />
      </div>

      {thumbnail ? (
        <div>
          <p>Thumbnail Preview:</p>
          <img src={thumbnail} alt="NFT Thumbnail" style={{ maxWidth: "300px" }} />
        </div>
      ) : (
        <p>No thumbnail available.</p>
      )}

      {minting && mintProgress && (
        <ProgressBar percentage={mintProgress.percentage} estimate={mintProgress.estimate} />
      )}

      <button onClick={handleMint} disabled={minting}>
        {minting ? "Minting..." : "Mint NFT"}
      </button>
    </div>
  );
};

export default OptionsScreen;
